import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  ArrowRight,
  CheckCircle,
  XCircle,
  Spinner,
  Lightning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    name: '',
    woo_url: '',
    woo_consumer_key: '',
    woo_consumer_secret: '',
    wp_app_password: '',
    wp_username: '',
    user_agent: 'Sortilize/1.0',
  });
  const navigate = useNavigate();

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  // If already onboarded, skip to dashboard
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/tenants/me`, { headers: getHeaders() });
        if (data.onboarded) navigate('/', { replace: true });
      } catch {}
    })();
  }, [getHeaders, navigate]);

  const testConnection = async () => {
    if (!config.woo_url || !config.woo_consumer_key || !config.woo_consumer_secret) {
      toast.error('Fyll inn URL + API-nøkler først');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/tenants/test-connection`, config, { headers: getHeaders() });
      setTestResult(data);
      if (data.success) toast.success(`Tilkoblet! ${data.total_products} produkter funnet.`);
      else toast.error(data.message);
    } catch (err) {
      toast.error('Test feilet');
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (!config.name || !testResult?.success) {
      toast.error('Butikknavn + vellykket test kreves');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/tenants/setup`, config, { headers: getHeaders() });
      toast.success('Butikk satt opp! Starter førstegangs-synkronisering...');
      // Trigger initial sync
      try {
        await axios.post(`${API}/api/sync/products`, {}, { headers: getHeaders() });
      } catch {}
      setTimeout(() => navigate('/', { replace: true }), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lagring feilet');
    } finally {
      setSaving(false);
    }
  };

  const stepProgress = (step / 3) * 100;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="onboarding-page">
      <div className="max-w-2xl w-full">
        <div className="flex items-center justify-center mb-6" data-testid="onboarding-logo">
          <Logo variant="full" height={40} />
        </div>

        <div className="mb-6">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Steg {step} av 3</span>
            <span>{Math.round(stepProgress)}%</span>
          </div>
          <Progress value={stepProgress} className="h-1.5" />
        </div>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">
              {step === 1 && 'Velkommen! La oss koble til butikken din'}
              {step === 2 && 'WooCommerce API-nøkler'}
              {step === 3 && 'Test og lagre'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {step === 1 && 'Vi starter med noen grunnleggende detaljer.'}
              {step === 2 && 'Lag API-nøkler i WooCommerce: WooCommerce → Settings → Advanced → REST API → Add Key (Read-Write).'}
              {step === 3 && 'Test tilkoblingen og vi er i gang!'}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <>
                <div>
                  <Label>Butikknavn</Label>
                  <Input
                    placeholder="F.eks. Min Garnbutikk"
                    value={config.name}
                    onChange={(e) => setConfig({ ...config, name: e.target.value })}
                    data-testid="onboarding-name"
                  />
                </div>
                <div>
                  <Label>WooCommerce URL</Label>
                  <Input
                    placeholder="https://minbutikk.no"
                    value={config.woo_url}
                    onChange={(e) => setConfig({ ...config, woo_url: e.target.value })}
                    data-testid="onboarding-woo-url"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Hjemmesiden (uten /wp-admin). Vi finner REST API-endepunktet automatisk.</p>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <Label>WooCommerce Consumer Key</Label>
                  <Input
                    placeholder="ck_..."
                    value={config.woo_consumer_key}
                    onChange={(e) => setConfig({ ...config, woo_consumer_key: e.target.value })}
                    data-testid="onboarding-consumer-key"
                  />
                </div>
                <div>
                  <Label>WooCommerce Consumer Secret</Label>
                  <Input
                    type="password"
                    placeholder="cs_..."
                    value={config.woo_consumer_secret}
                    onChange={(e) => setConfig({ ...config, woo_consumer_secret: e.target.value })}
                    data-testid="onboarding-consumer-secret"
                  />
                </div>

                <div className="border-t border-border pt-4 mt-4">
                  <p className="text-sm font-semibold mb-2">Valgfritt: WordPress Application Password</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Trengs for Media-skanner (finner ubrukte bilder). Lag i WP Admin → Users → Edit profile → Application Passwords.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="WP brukernavn"
                      value={config.wp_username}
                      onChange={(e) => setConfig({ ...config, wp_username: e.target.value })}
                      data-testid="onboarding-wp-username"
                    />
                    <Input
                      type="password"
                      placeholder="App Password"
                      value={config.wp_app_password}
                      onChange={(e) => setConfig({ ...config, wp_app_password: e.target.value })}
                      data-testid="onboarding-wp-password"
                    />
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <p className="text-sm font-semibold mb-2">Valgfritt: Custom User-Agent</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Hvis butikken din står bak Cloudflare og blokkerer default user-agents. Kontakt din utvikler for å whitelist denne strengen.
                  </p>
                  <Input
                    value={config.user_agent}
                    onChange={(e) => setConfig({ ...config, user_agent: e.target.value })}
                    data-testid="onboarding-user-agent"
                  />
                </div>
              </>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <Button
                  onClick={testConnection}
                  disabled={testing}
                  size="lg"
                  className="w-full gap-2"
                  variant="outline"
                  data-testid="onboarding-test-btn"
                >
                  {testing ? (<><Spinner size={16} className="animate-spin" /> Tester...</>) : (<><Lightning size={16} weight="bold" /> Test tilkobling</>)}
                </Button>

                {testResult && (
                  <Card className={testResult.success ? 'border-success/40 bg-success/5' : 'border-destructive/40 bg-destructive/5'}>
                    <CardContent className="p-4 flex items-start gap-3">
                      {testResult.success
                        ? <CheckCircle size={24} weight="fill" className="text-success shrink-0" />
                        : <XCircle size={24} weight="fill" className="text-destructive shrink-0" />}
                      <div>
                        <p className="font-semibold text-sm">{testResult.success ? 'Tilkobling vellykket!' : 'Tilkobling feilet'}</p>
                        <p className="text-xs text-muted-foreground mt-1">{testResult.message}</p>
                        {testResult.success && testResult.total_products && (
                          <p className="text-xs mt-2">Fant <strong>{testResult.total_products}</strong> produkter klare for synkronisering.</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="bg-muted/40 p-4 rounded-md text-xs space-y-1">
                  <p><strong>Hva skjer når du lagrer:</strong></p>
                  <p>1. API-nøklene lagres kryptert på serveren vår</p>
                  <p>2. Vi starter førstegangs-synkronisering av produkter (tar 2-15 min basert på størrelse)</p>
                  <p>3. Du får tilgang til dashboard og alle funksjoner</p>
                  <p>4. AutoGuard kan startes manuelt når synk er ferdig</p>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t border-border">
              <Button
                variant="ghost"
                onClick={() => setStep(step - 1)}
                disabled={step === 1}
                data-testid="onboarding-back-btn"
              >
                Tilbake
              </Button>
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={(step === 1 && (!config.name || !config.woo_url)) || (step === 2 && (!config.woo_consumer_key || !config.woo_consumer_secret))}
                  className="gap-2"
                  data-testid="onboarding-next-btn"
                >
                  Neste <ArrowRight size={14} />
                </Button>
              ) : (
                <Button
                  onClick={save}
                  disabled={saving || !testResult?.success}
                  className="gap-2"
                  data-testid="onboarding-save-btn"
                >
                  {saving ? 'Lagrer...' : 'Fullfør oppsett'}
                  {!saving && <CheckCircle size={14} />}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
